import { writeToAll, readAndResolve } from './helpers/cache'
import { getS2SBeta } from '../prebidFacade/s2sAdapter/BetaTest'

const SOURCE_URL = process.env.S2S_URL

export namespace Usersync {
  export const init = async (): Promise<void> => {
    window.addEventListener('message', windowMessageHandler, false)

    const slug = window['$mediavine'].web.model.slug
    const url = await getUsersyncUrl(slug)
    insertUserSyncIframe(url)
  }
}

const windowMessageHandler = (evt: MessageEvent): void => {
  const iframePromises: Array<Promise<void>> = []

  if (evt.origin === SOURCE_URL) {
    if (evt.data) {
      // Save tokens everywhere we can
      writeToAll('mv_tokens', evt.data.tokens)
      const syncs: Array<ISync> = evt.data.syncUrls || []
      syncs.forEach((sync) => iframePromises.push(createUsersyncElement(sync)))
    }
  }
}

export async function createUsersyncElement({
  type,
  url
}: ISync): Promise<void> {
  return new Promise<void>((yay) => {
    type === 'iframe'
      ? insertUserSyncIframe(url, yay)
      : insertUserSyncPixel(url, yay)
  })
}

export async function getConsentString(cmp: Function): Promise<string> {
  return new Promise((res, rej) => {
    cmp('getConsentData', '', (consentData: IConsentData) => {
      if (!consentData.gdprApplies) {
        res('')
      } else {
        res(consentData.consentData)
      }
    })
  })
}

export async function getUsersyncUrl(slug: string): Promise<string> {
  let src = `${SOURCE_URL}/usersync/sync?origin=${
    document.location.origin
  }&src=${SOURCE_URL}&slug=${slug}&s2sVersion=${getS2SBeta().name}`

  const existingTokens: { mv_uuid?: string } | null = await readAndResolve(
    'mv_tokens'
  )

  // Add user's mediavine uuid
  if (existingTokens && existingTokens.mv_uuid) {
    src = `${src}&mv_uuid=${existingTokens.mv_uuid}`
  }

  // Handles GDPR Data if present
  if (window.__cmp) {
    const consentString = await getConsentString(window.__cmp)
    src += `&gdpr_consent=${consentString}`
  }

  return src
}

/*
 * Inserts an element(elm) as targets child, by default as first child
 * @param {HTMLElement} elm
 * @param {HTMLElement} [doc]
 * @param {HTMLElement} [target]
 * @param {Boolean} [asLastChildChild]
 * @return {HTMLElement}
 */
export function insertElement(elm, doc, target, asLastChildChild) {
  doc = doc || document
  let parentEl
  if (target) {
    parentEl = doc.getElementsByTagName(target)
  } else {
    parentEl = doc.getElementsByTagName('head')
  }
  try {
    parentEl = parentEl.length ? parentEl : doc.getElementsByTagName('body')
    if (parentEl.length) {
      parentEl = parentEl[0]
      const insertBeforeEl = asLastChildChild ? null : parentEl.firstChild
      return parentEl.insertBefore(elm, insertBeforeEl)
    }
  } catch (e) {
    /* noop */
  }
}

export function insertUserSyncIframe(url: string, done?) {
  const iframeHtml = createTrackPixelIframeHtml(
    url,
    false,
    'allow-scripts allow-same-origin'
  )
  const div = document.createElement('div')
  div.innerHTML = iframeHtml
  const iframe = div.firstChild
  if (done) {
    // @ts-ignore
    iframe.addEventListener('load', done as any)
    // @ts-ignore
    iframe.addEventListener('error', done as any)
  }
  insertElement(iframe, document, 'html', true)
}

export function insertUserSyncPixel(url, done?) {
  const pixelHtml = createTrackPixelHtml(url)
  const div = document.createElement('div')
  div.innerHTML = pixelHtml
  const pixel = div.firstChild
  if (done) {
    // @ts-ignore
    pixel.addEventListener('load', done)
    // @ts-ignore
    pixel.addEventListener('error', done)
  }
  insertElement(pixel, document, 'html', true)
}

export function createTrackPixelIframeHtml(
  url,
  encodeUri = true,
  sandbox = ''
) {
  if (!url) {
    return ''
  }
  if (encodeUri) {
    url = encodeURI(url)
  }
  if (sandbox) {
    sandbox = `sandbox="${sandbox}"`
  }

  return `<iframe ${sandbox}"
      frameborder="0"
      allowtransparency="true"
      marginheight="0" marginwidth="0"
      width="0" hspace="0" vspace="0" height="0"
      style="height:0px;width:0px;display:none;"
      scrolling="no"
      src="${url}">
    </iframe>`
}

/**
 * Creates a snippet of HTML that retrieves the specified `url`
 * @param  {string} url URL to be requested
 * @return {string}     HTML snippet that contains the img src = set to `url`
 */
export function createTrackPixelHtml(url) {
  if (!url) {
    return ''
  }

  const escapedUrl = encodeURI(url)
  let img =
    '<div style="position:absolute;left:0px;top:0px;visibility:hidden;">'
  img += '<img src="' + escapedUrl + '"></div>'
  return img
}

export function isSafariBrowser() {
  return /^((?!chrome|android).)*safari/i.test(navigator.userAgent)
}

/**
 * Inserts an image pixel with the specified `url` for cookie sync
 * @param {string} url URL string of the image pixel to load
 * @param  {function} [done] an optional exit callback, used when this usersync pixel is added during an async process
 */
export function triggerPixel(url) {
  const img = new Image()
  img.src = `${url}&type=pixel`
}

interface ISync {
  url: string
  type: string
}

interface IConsentData {
  consentData: string
  gdprApplies: boolean
  hasGlobalScope: boolean
}
